import { useTranslation } from 'react-i18next';

import {
  Form,
  Loader,
  PLATFORM_THEME_COLORS,
  SelectInput,
  TextInput,
} from '@thinknet-gmbh/platform-common-ui';

import { displayAddress } from 'src/common/helpers/common.helpers';
import { useGetAllPlatformCompanies } from 'src/hooks/companies/get-all-platform-companies.hook';
import { useGetPlatformCompanyBusinessAddresses } from 'src/hooks/companies/get-platform-company-business-addresses.hook';
import { useGetPlatformCompanyDepartments } from 'src/hooks/companies/get-platform-company-departments.hook';
import { useGetPlatformCompanyLocations } from 'src/hooks/companies/get-platform-company-locations.hook';
import { TitleRowLabel, StyledContainer, StyledRowContainer } from '../../employee-common.styles';


const CompanyInformation= () => {
  const { t } = useTranslation(['common', 'employee', 'settings']);

  const { companies, loading: companiesLoading } = useGetAllPlatformCompanies();
  const { companyLocations, loading: companyLocationsLoading } = useGetPlatformCompanyLocations();
  const {
    companyBusinessAddresses,
    loading: companyBusinessAddressesLoading,
  } = useGetPlatformCompanyBusinessAddresses();
  const {
    companyDepartments,
    loading: companyDepartmentsLoading,
  } = useGetPlatformCompanyDepartments();

  const loading =
    companiesLoading ||
    companyLocationsLoading ||
    companyBusinessAddressesLoading ||
    companyDepartmentsLoading;

  if (loading) {
    return <Loader />;
  }

  return (
    <div>
      <StyledContainer>
        <TitleRowLabel weight="bold" fontColor={PLATFORM_THEME_COLORS.grayVariations.dark}>
          {t('employee:workingConditions.labels.companyAndLocation')}
        </TitleRowLabel>
        <StyledRowContainer>
          <Form.Item
            name={'companyId'}
            label={t('settings:company.labels.companyName')}
            rules={[
              {
                required: true,
                message: t('settings:company.exceptions.requiredCompanyName'),
              },
            ]}
          >
            <SelectInput placeholder={t('common:placeholders.selectFromList')}>
              {companies.map((company: any) => (
                <SelectInput.Option key={company._id} value={company._id}>
                  {company.name}
                </SelectInput.Option>
              ))}
            </SelectInput>
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const currCompanyLocation = companyLocations.find(
                (location: any) => location._id === getFieldValue('companyLocationId')
              );

              if (
                currCompanyLocation &&
                currCompanyLocation.companyId !== getFieldValue('companyId')
              ) {
                setFieldsValue({ companyLocationId: null });
              }

              return (
                <Form.Item
                  name={'companyLocationId'}
                  label={t('common:labels.location')}
                  rules={[
                    {
                      required: true,
                      message: t('common:validation.error.locationRequired'),
                    },
                  ]}
                >
                  <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                    {companyLocations
                      .filter((location: any) => location.companyId === getFieldValue('companyId'))
                      .map((location: any) => (
                        <SelectInput.Option key={location._id} value={location._id}>
                          {location.name}
                        </SelectInput.Option>
                      ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const currBusinessAddress = companyBusinessAddresses.find(
                (location: any) => location._id === getFieldValue('companyBusinessAddressId')
              );

              if (
                currBusinessAddress &&
                currBusinessAddress.companyLocationId !== getFieldValue('companyLocationId')
              ) {
                setFieldsValue({ companyBusinessAddressId: null });
              }

              return (
                <Form.Item
                  name={'companyBusinessAddressId'}
                  label={t('common:labels.businessAddress')}
                  rules={[
                    {
                      required: true,
                      message: t('common:validation.error.locationRequired'),
                    },
                  ]}
                >
                  <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                    {companyBusinessAddresses
                      .filter(
                        (businessAddress: any) =>
                          businessAddress.companyLocationId === getFieldValue('companyLocationId')
                      )
                      .map((businessAddress: any) => (
                        <SelectInput.Option key={businessAddress._id} value={businessAddress._id}>
                          {displayAddress(businessAddress)}
                        </SelectInput.Option>
                      ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue, setFieldsValue }) => {
              const currDepartment = companyDepartments.find(
                (department: any) => department._id === getFieldValue('companyDepartmentId')
              );

              if (
                currDepartment &&
                currDepartment.companyBusinessAddressId !==
                  getFieldValue('companyBusinessAddressId')
              ) {
                setFieldsValue({ companyDepartmentId: null });
              }

              return (
                <Form.Item
                  name={'companyDepartmentId'}
                  label={t('common:labels.department')}
                  rules={[
                    {
                      required: true,
                      message: t('common:validation.error.locationRequired'),
                    },
                  ]}
                >
                  <SelectInput placeholder={t('common:placeholders.selectFromList')}>
                    {companyDepartments
                      .filter(
                        (department: any) =>
                          department.companyBusinessAddressId ===
                          getFieldValue('companyBusinessAddressId')
                      )
                      .map((department: any) => (
                        <SelectInput.Option key={department._id} value={department._id}>
                          {department.name}
                        </SelectInput.Option>
                      ))}
                  </SelectInput>
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item label={t('employee:labels.costCenter')} shouldUpdate>
            {({ getFieldValue }) => (
              <TextInput
                disabled
                value={
                  companyDepartments.find(
                    (department: any) => department._id === getFieldValue('companyDepartmentId')
                  )?.costCenter
                }
                maxLength={50}
              />
            )}
          </Form.Item>
        </StyledRowContainer>
      </StyledContainer>
    </div>
  );
};

export default CompanyInformation;
